import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { animate, createScope, stagger, type Scope } from "animejs";

const TIMER_SECONDS = 5 * 60;
const RING_SIZE = 340;
const RING_STROKE = 10;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

export function Anime() {
  const rootRef = useRef<HTMLDivElement>(null);
  const scopeRef = useRef<Scope | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);
  const [isRunning, setIsRunning] = useState(false);

  const progress = 1 - secondsLeft / TIMER_SECONDS;
  const ringOffset = RING_LENGTH * (1 - progress);
  const timeParts = useMemo(() => formatTime(secondsLeft).split(""), [secondsLeft]);

  useEffect(() => {
    scopeRef.current = createScope({ root: rootRef }).add(() => {
      animate(".timer-shell", {
        opacity: [0, 1],
        y: [28, 0],
        scale: [0.97, 1],
        duration: 900,
        ease: "outExpo",
      });

      animate(".timer-orbit", {
        rotate: "1turn",
        duration: 18000,
        loop: true,
        ease: "linear",
      });

      animate(".timer-prism", {
        scale: [0.86, 1.08],
        opacity: [0.34, 0.72],
        delay: stagger(180, { from: "center" }),
        duration: 1800,
        alternate: true,
        loop: true,
        ease: "inOutSine",
      });

      animate(".timer-tick", {
        opacity: [0.28, 0.9],
        delay: stagger(34),
        duration: 900,
        alternate: true,
        loop: true,
        ease: "inOutSine",
      });
    });

    return () => {
      scopeRef.current?.revert();
      scopeRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isRunning || secondsLeft === 0) {
      return;
    }

    const timerId = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          setIsRunning(false);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [isRunning, secondsLeft]);

  useEffect(() => {
    scopeRef.current?.execute(() => {
      animate(".timer-progress", {
        strokeDashoffset: ringOffset,
        duration: 650,
        ease: "outCubic",
      });

      animate(".timer-digit", {
        y: [-6, 0],
        opacity: [0.76, 1],
        delay: stagger(22, { from: "center" }),
        duration: 360,
        ease: "outBack",
      });
    });
  }, [ringOffset, secondsLeft]);

  const toggleTimer = () => {
    setIsRunning((current) => !current);

    scopeRef.current?.execute(() => {
      animate(".timer-core", {
        scale: [0.98, 1],
        duration: 420,
        ease: "outBack",
      });
    });
  };

  const resetTimer = () => {
    setIsRunning(false);
    setSecondsLeft(TIMER_SECONDS);

    scopeRef.current?.execute(() => {
      animate(".timer-shell", {
        rotate: [-0.8, 0],
        duration: 520,
        ease: "outBack",
      });
    });
  };

  return (
    <main ref={rootRef} style={styles.page}>
      <section className="timer-shell" style={styles.shell}>
        <div style={styles.header}>
          <div>
            {/* <p style={styles.eyebrow}>Anime.js + React</p> */}
            {/* <h2 style={styles.title}>Deep Focus Timer</h2> */}
          </div>
          <div style={styles.status}>{isRunning ? "Running" : "Paused"}</div>
        </div>

        <div style={styles.stage}>
          <div className="timer-orbit" style={styles.orbit}>
            {Array.from({ length: 3 }, (_, index) => (
              <span
                key={index}
                className="timer-prism"
                style={{
                  ...styles.prism,
                  transform: `rotate(${index * 120}deg) translateX(150px)`,
                }}
              />
            ))}
          </div>

          <div style={styles.tickRing}>
            {Array.from({ length: 48 }, (_, index) => (
              <span
                key={index}
                className="timer-tick"
                style={{
                  ...styles.tick,
                  height: index % 4 === 0 ? "18px" : "10px",
                  opacity: index % 4 === 0 ? 0.72 : 0.34,
                  transform: `rotate(${index * 7.5}deg) translateY(-178px)`,
                }}
              />
            ))}
          </div>

          <svg
            width={RING_SIZE}
            height={RING_SIZE}
            viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
            style={styles.svg}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="timerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f6d8ff" />
                <stop offset="28%" stopColor="#b45cff" />
                <stop offset="58%" stopColor="#6c2bd9" />
                <stop offset="82%" stopColor="#241057" />
                <stop offset="100%" stopColor="#65f4d9" />
              </linearGradient>
            </defs>
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="rgba(174, 130, 255, 0.16)"
              strokeWidth={RING_STROKE}
            />
            <circle
              className="timer-progress"
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_RADIUS}
              fill="none"
              stroke="url(#timerGradient)"
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_LENGTH}
              strokeDashoffset={ringOffset}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
            />
          </svg>

          <div className="timer-core" style={styles.core}>
            <div style={styles.time} aria-label={`${formatTime(secondsLeft)} remaining`}>
              {timeParts.map((part, index) => (
                <span
                  key={`${part}-${index}`}
                  className="timer-digit"
                  style={part === ":" ? styles.separator : styles.digit}
                >
                  {part}
                </span>
              ))}
            </div>
            <div style={styles.meta}>
              {Math.round(progress * 100)}% elapsed
            </div>
          </div>
        </div>

        <div style={styles.controls}>
          <button type="button" onClick={toggleTimer} style={styles.primaryButton}>
            {isRunning ? "Pause" : "Start"}
          </button>
          <button type="button" onClick={resetTimer} style={styles.secondaryButton}>
            Reset
          </button>
        </div>
      </section>
    </main>
  );
}

const styles = {
  page: {
    minHeight: "calc(100vh - 96px)",
    display: "grid",
    placeItems: "center",
    padding: "32px 16px",
    color: "#f7edff",
    background:
      "radial-gradient(circle at 20% 12%, rgba(153, 88, 255, 0.28), transparent 32%), linear-gradient(145deg, #070413 0%, #130825 38%, #1b0a34 68%, #05030d 100%)",
  },
  shell: {
    width: "min(860px, 100%)",
    padding: "24px",
    border: "1px solid rgba(218, 187, 255, 0.22)",
    borderRadius: "8px",
    background:
      "linear-gradient(145deg, rgba(23, 12, 49, 0.92), rgba(11, 7, 26, 0.94))",
    boxShadow:
      "0 28px 90px rgba(14, 2, 36, 0.58), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
    opacity: 0,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  eyebrow: {
    margin: "0 0 6px",
    color: "#bca6d9",
    fontSize: "13px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    lineHeight: 1.1,
  },
  status: {
    minWidth: "92px",
    padding: "8px 12px",
    border: "1px solid rgba(174, 130, 255, 0.32)",
    borderRadius: "8px",
    color: "#d9c6ff",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: 700,
  },
  stage: {
    position: "relative",
    minHeight: "460px",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    border: "1px solid rgba(164, 112, 255, 0.18)",
    borderRadius: "8px",
    background:
      "radial-gradient(circle, rgba(84, 36, 158, 0.62) 0%, rgba(28, 10, 62, 0.7) 38%, rgba(7, 4, 18, 0.96) 72%)",
  },
  orbit: {
    position: "absolute",
    width: "320px",
    height: "320px",
    borderRadius: "999px",
    transformOrigin: "50% 50%",
    filter: "blur(0.2px)",
  },
  prism: {
    position: "absolute",
    top: "148px",
    left: "148px",
    width: "24px",
    height: "24px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #f7d7ff, #8e4dff 48%, #1b0a4d)",
    boxShadow: "0 0 32px rgba(192, 105, 255, 0.62)",
    transformOrigin: "12px 12px",
  },
  tickRing: {
    position: "absolute",
    width: "360px",
    height: "360px",
    borderRadius: "999px",
  },
  tick: {
    position: "absolute",
    top: "171px",
    left: "178px",
    width: "2px",
    borderRadius: "999px",
    background: "linear-gradient(#efe2ff, rgba(122, 64, 255, 0.24))",
    transformOrigin: "1px 9px",
  },
  svg: {
    position: "relative",
    zIndex: 2,
    overflow: "visible",
    filter: "drop-shadow(0 0 22px rgba(160, 88, 255, 0.44))",
  },
  core: {
    position: "absolute",
    zIndex: 3,
    display: "grid",
    placeItems: "center",
    width: "230px",
    height: "230px",
    borderRadius: "999px",
    border: "1px solid rgba(232, 210, 255, 0.2)",
    background:
      "radial-gradient(circle at 42% 32%, rgba(254, 231, 255, 0.14), rgba(55, 20, 110, 0.38) 42%, rgba(9, 4, 26, 0.92) 78%)",
    boxShadow:
      "inset 0 0 40px rgba(205, 161, 255, 0.16), 0 20px 70px rgba(4, 1, 15, 0.68)",
  },
  time: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "center",
    minWidth: "180px",
    fontVariantNumeric: "tabular-nums",
  },
  digit: {
    display: "inline-block",
    minWidth: "36px",
    color: "#fff5ff",
    fontSize: "52px",
    fontWeight: 800,
    lineHeight: 1,
    textAlign: "center",
    textShadow: "0 0 28px rgba(215, 144, 255, 0.6)",
  },
  separator: {
    display: "inline-block",
    minWidth: "20px",
    color: "#a987ff",
    fontSize: "46px",
    fontWeight: 800,
    lineHeight: 1,
    textAlign: "center",
  },
  meta: {
    marginTop: "12px",
    color: "#bfa7dc",
    fontSize: "13px",
    fontWeight: 700,
  },
  controls: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginTop: "18px",
  },
  primaryButton: {
    minWidth: "112px",
    border: "1px solid rgba(234, 211, 255, 0.42)",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #d7a8ff, #7a35f2 52%, #2a126b)",
    color: "#ffffff",
    cursor: "pointer",
    fontWeight: 800,
    padding: "11px 16px",
    boxShadow: "0 10px 24px rgba(89, 39, 201, 0.38)",
  },
  secondaryButton: {
    minWidth: "112px",
    border: "1px solid rgba(181, 144, 240, 0.34)",
    borderRadius: "8px",
    background: "rgba(20, 9, 43, 0.72)",
    color: "#decaff",
    cursor: "pointer",
    fontWeight: 800,
    padding: "11px 16px",
  },
} satisfies Record<string, CSSProperties>;
